import { useState, useEffect, useRef } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Building2, ShoppingBag, Users, Hammer, Sparkles, Wind,
  ClipboardCheck, Camera, UserCheck, CalendarClock,
  Phone, Mail, MapPin, ArrowRight, Check, Quote,
} from "lucide-react";
import heroImg from "@/assets/hero-cleaner.jpg";
import homepageCleanerImg from "@/assets/homepage-cleaner.png";
import westworthLogo from "@/assets/logo-client-westworth.jpg";
import oceanLogo from "@/assets/logo-client-ocean.png";
import krownLogo from "@/assets/logo-client-krown.png";
import oceanTrailerLogo from "@/assets/logo-client-ocean-trailer.jpg";
import trellisLogo from "@/assets/logo-client-trellis.jpg";
import pacificPalletLogo from "@/assets/logo-client-pacific-pallet.png";
import everestLogo from "@/assets/logo-client-everest.png";
import { Layout } from "@/components/Layout";

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: "Aureline Facility Services" },
      { name: "description", content: "Commercial, retail, strata and specialty cleaning across the Lower Mainland. Verified visits, photo-based reporting, and supervised teams." },
    ],
  }),
});

const services = [
  { id: "commercial-office", n: "01", icon: Building2, title: "Commercial Office", desc: "Janitorial programs tracked via digital site checklists for consistent quality.", color: "mint" },
  { id: "retail-cleaning", n: "02", icon: ShoppingBag, title: "Retail Cleaning", desc: "Storefront and customer areas maintained with real-time digital verification.", color: "sky" },
  { id: "strata-common-areas", n: "03", icon: Users, title: "Strata & Common Areas", desc: "Digital audit logs of lobbies, hallways, and elevators for strata board visibility.", color: "deep" },
  { id: "post-construction", n: "04", icon: Hammer, title: "Post-Construction", desc: "Detail cleanups and dust containment documented with photo verification.", color: "sky" },
  { id: "floor-surface-care", n: "05", icon: Sparkles, title: "Floor & Surface Care", desc: "Precision surface treatments and touch-point sanitization tracked in real-time.", color: "mint" },
  { id: "residential", n: "06", icon: Wind, title: "Residential", desc: "Premium home deep cleaning and sanitization scheduled and confirmed via app.", color: "deep" },
];

const reasons = [
  { icon: ClipboardCheck, title: "Digital audit checklists", desc: "App-driven, site-specific checklists custom-engineered for your property.", color: "mint" },
  { icon: Camera, title: "Real-time reports", desc: "Instantly delivered digital inspection reports with GPS and photo verification.", color: "sky" },
  { icon: UserCheck, title: "Vetted tech-enabled crews", desc: "Supervised teams equipped with modern operations tracking and communication tools.", color: "deep" },
  { icon: CalendarClock, title: "Smart scheduling", desc: "Algorithmic scheduling checks and automated shift confirmations to guarantee coverage.", color: "ice" },
];

function AnimatedNumber({ value, suffix = "", duration = 3000 }: { value: number; suffix?: string; duration?: number }) {
  const [current, setCurrent] = useState(0);
  const elementRef = useRef<HTMLSpanElement>(null);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasStarted) return;

    let startTimestamp: number | null = null;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easedProgress = progress * (2 - progress); // easeOutQuad
      setCurrent(Math.floor(easedProgress * value));

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };

    window.requestAnimationFrame(step);
  }, [hasStarted, value, duration]);

  const renderValue = () => {
    if (suffix === "special-sq") {
      if (current < 999) {
        return (
          <>
            {current}
            <span className="text-xl md:text-2xl xl:text-3xl ml-1 font-medium opacity-90 inline-block align-baseline whitespace-nowrap">k</span>
          </>
        );
      }
      return (
        <>
          1M+
          <span className="text-xl md:text-2xl xl:text-3xl ml-2 font-medium opacity-90 inline-block align-baseline whitespace-nowrap">Sq</span>
        </>
      );
    }

    const renderSuffix = () => {
      if (suffix.includes("sq. ft.")) {
        const mainSuffix = suffix.replace("sq. ft.", "").trim();
        return (
          <>
            {mainSuffix}
            <span className="text-xl md:text-2xl xl:text-3xl ml-2 font-medium opacity-90 inline-block align-baseline whitespace-nowrap">sq. ft.</span>
          </>
        );
      }
      return suffix;
    };

    return (
      <>
        {current}
        {renderSuffix()}
      </>
    );
  };

  return <span ref={elementRef}>{renderValue()}</span>;
}

const stats = [
  { value: 150, suffix: "+", label: "Sites serviced", bg: "var(--navy-deep)", fg: "#fff" },
  { value: 999, suffix: "special-sq", label: "Square feet cleaned monthly", bg: "var(--sky)", fg: "var(--navy-deep)" },
  { value: 100, suffix: "+", label: "Dedicated staff", bg: "var(--mint)", fg: "var(--navy-deep)" },
];

const sectors = ["Commercial Buildings", "Retail", "Strata Councils", "Specialty Spaces"];

const clientLogos = [
  { src: westworthLogo, alt: "Westworth Financial", className: "h-12 md:h-14" },
  { src: oceanLogo, alt: "Klondike Cold Storage", className: "h-12 md:h-14" },
  { src: krownLogo, alt: "Krown", className: "h-14 md:h-16" },
  { src: oceanTrailerLogo, alt: "Ocean Trailer", className: "h-12 md:h-14" },
  { src: trellisLogo, alt: "Trellis Properties", className: "h-16 md:h-18" },
  { src: pacificPalletLogo, alt: "Pacific Pallet", className: "h-14 md:h-16" },
  { src: everestLogo, alt: "Everest Group", className: "h-18 md:h-20" },
];

function Index() {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    propertyType: "Commercial office",
    notes: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || "https://creative-confluence-hub.onrender.com/api/contact";
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Something went wrong.");
      }

      setSuccess(true);
      setFormData({
        name: "",
        company: "",
        email: "",
        phone: "",
        propertyType: "Commercial office",
        notes: ""
      });
    } catch (err: any) {
      setError(err.message || "Failed to submit form. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden gradient-hero min-h-[calc(100vh-96px)] lg:h-[calc(100vh-96px)] flex flex-col justify-between">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 py-8 lg:py-12 grid lg:grid-cols-12 gap-12 xl:gap-20 items-center w-full flex-grow overflow-hidden">
          <div className="lg:col-span-6 relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-mint text-navy eyebrow text-[10px] sm:text-xs whitespace-nowrap animate-fade-up">
              <span className="w-1.5 h-1.5 rounded-full bg-sky shrink-0" /> Powered by Quality Inspections
            </div>
            <h1 className="mt-7 font-display text-4xl md:text-6xl xl:text-7xl text-navy leading-[1.2] tracking-tight animate-fade-up delay-100">
              Elevated service.<br />
              <span className="italic text-navy-soft">Practical</span> pricing.
            </h1>
            <p className="mt-6 text-lg xl:text-xl text-muted-foreground max-w-lg xl:max-w-2xl leading-relaxed animate-fade-up delay-200">
              Aureline is a premium facility services firm engineered for trust, transparency, and operational consistency. Combining supervised execution with real-time digital reporting, we deliver high-quality property maintenance solutions across British Columbia.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 animate-fade-up delay-300">
              <Button asChild className="rounded-full h-12 px-6 bg-navy text-primary-foreground hover:bg-navy/90 cursor-pointer">
                <a href="#contact">
                  Request a customized quote <ArrowRight />
                </a>
              </Button>
              <Button asChild variant="outline" className="rounded-full h-12 px-6 border-navy/20 hover:bg-secondary cursor-pointer">
                <Link to="/services">
                  Explore services
                </Link>
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-muted-foreground animate-fade-up delay-400">
              {["Quality inspections", "Detailed reporting", "Supervised teams"].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-mint flex items-center justify-center">
                    <Check className="w-3 h-3 text-mint-ink font-bold" />
                  </span>
                  {t}
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 relative h-full hidden lg:flex items-center justify-center">
            {/* Diagonal stripe accent — Everclean homage */}
            <div className="absolute -top-6 -right-12 w-72 h-[120%] stripe-accent opacity-90 hidden md:block" aria-hidden />
            
            <div className="relative shadow-elevated animate-scale-in w-full rounded-2xl">
              <img
                src={heroImg}
                alt="Professional cleaner servicing a modern office"
                width={1280}
                height={1280}
                className="w-full h-[380px] lg:h-[420px] xl:h-[480px] 2xl:h-[580px] object-cover rounded-2xl"
              />
              <div className="absolute -top-6 -left-6 z-10 bg-card rounded-xl shadow-elevated p-5 w-64 hidden md:block animate-scale-in delay-200">
                <div className="eyebrow text-navy-soft">Site Inspection</div>
                <ul className="mt-3 space-y-2 text-sm">
                  {["Floors", "Surfaces", "Washrooms", "High-touch sites"].map((t) => (
                    <li key={t} className="flex items-center justify-between">
                      <span className="text-foreground">{t}</span>
                      <span className="w-4.5 h-4.5 rounded-full bg-mint/55 flex items-center justify-center">
                        <Check className="w-3 h-3 text-mint-ink" />
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Sub-bar */}
        <div className="bg-navy">
          <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 py-1 xl:py-2 flex flex-wrap items-center justify-center gap-x-10 xl:gap-x-16 gap-y-2 text-primary-foreground/85 text-sm xl:text-base">
            {["Reliable communication", "Responsive operations", "Detailed oversight", "Consistent follow-through"].map((t, i) => (
              <div key={t} className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-sky" /> {t}
                {i < 3 && <span className="hidden md:inline w-px h-4 bg-primary-foreground/20 ml-7" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/*
      <section className="py-16 bg-cream/40 border-b border-border/40 overflow-hidden">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6">
          <p className="text-center text-sm md:text-base font-semibold uppercase tracking-widest text-muted-foreground mb-12 select-none">
            Trusted by leading companies
          </p>
          <div className="relative w-full overflow-hidden">
            <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[#FAFBFB] via-[#FAFBFB]/80 to-transparent z-10 pointer-events-none" />
            <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[#FAFBFB] via-[#FAFBFB]/80 to-transparent z-10 pointer-events-none" />
            
            <div className="animate-marquee flex items-center gap-16 md:gap-24 opacity-75 grayscale hover:grayscale-0 transition-all duration-300 select-none py-2">
              {[...clientLogos, ...clientLogos, ...clientLogos].map((logo, idx) => (
                <img 
                  key={idx} 
                  src={logo.src} 
                  alt={logo.alt} 
                  className={`${logo.className} w-auto object-contain shrink-0`} 
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      */}

      {/* STATS — Simpurgo homage, refined */}
      <section className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 mt-12 relative z-10">
        <div className="grid md:grid-cols-3 gap-4 xl:gap-6">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-2xl p-8 xl:p-12 shadow-elevated hover-lift"
              style={{ background: s.bg, color: s.fg }}
            >
              <div className="font-display text-6xl xl:text-7xl 2xl:text-8xl leading-none whitespace-nowrap">
                <AnimatedNumber value={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-3 text-sm xl:text-base opacity-85">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-28">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6">
          <div className="max-w-2xl xl:max-w-4xl">
            <div className="eyebrow text-navy-soft">Services Offered</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl xl:text-6xl text-navy tracking-tight">
              Professional cleaning solutions for every space.
            </h2>
            <p className="mt-4 text-muted-foreground xl:text-lg">
              From routine janitorial to specialty detail, every program is built
              around the property and the people who use it.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5 xl:gap-8">
            {services.map((s) => (
              <Link
                key={s.title}
                to="/services/$serviceId"
                params={{ serviceId: s.id }}
                className={`group relative bg-card rounded-2xl p-7 xl:p-10 shadow-card border border-border/60 hover:shadow-elevated hover-lift transition block border-t-4 ${
                  s.color === "mint" ? "border-t-mint" :
                  s.color === "sky" ? "border-t-sky" :
                  "border-t-navy-soft"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 ${
                    s.color === "mint" ? "bg-mint/30 text-navy-deep group-hover:bg-mint group-hover:text-mint-ink" :
                    s.color === "sky" ? "bg-sky/30 text-navy-deep group-hover:bg-sky group-hover:text-navy-deep" :
                    "bg-secondary/70 text-navy group-hover:bg-navy-soft group-hover:text-white"
                  }`}>
                    <s.icon className="w-5 h-5" />
                  </div>
                  <span className="font-display text-sm text-muted-foreground">{s.n}</span>
                </div>
                <h3 className="mt-6 text-xl text-navy transition-colors group-hover:text-navy-soft">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed transition-colors group-hover:text-navy-deep/85">{s.desc}</p>
                <div className="mt-6 flex items-center gap-2 text-sm text-navy-soft font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300">
                  Learn more <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-28 bg-cream">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 grid lg:grid-cols-2 gap-16 xl:gap-24 items-center">
          <div className="relative hidden lg:block">
            <div className="absolute -top-6 -left-6 w-40 h-40 stripe-accent rounded-2xl hidden md:block" aria-hidden />
            <img
              src={homepageCleanerImg}
              alt="Cleaner in marble lobby"
              width={1280}
              height={1280}
              className="relative rounded-2xl overflow-hidden shadow-elevated w-full h-[480px] xl:h-[600px] 2xl:h-[700px] object-cover"
            />
          </div>
          <div>
            <div className="eyebrow text-navy-soft">About Aureline</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl xl:text-6xl text-navy leading-tight tracking-tight">
              Tailored cleaning programs for every site.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed xl:text-lg">
              We support commercial offices, retail, strata, and mixed-use properties
              with trained teams, practical processes, and pricing that makes sense.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed xl:text-lg">
              Quality matters. That's why we use real-time digital inspection reports to verify
              standards, track issues, and deliver clear visibility — so you always know
              the work was done and the results are consistent.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 bg-card rounded-full pl-2 pr-5 py-2 shadow-card">
              <span className="w-8 h-8 rounded-full bg-mint flex items-center justify-center">
                <Check className="w-4 h-4 text-mint-ink font-bold" />
              </span>
              <span className="text-sm text-navy">
                Proudly serving British Columbia, Coast to Interior.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" className="py-28">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-end">
            <div>
              <div className="eyebrow text-navy-soft">Why Aureline</div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl xl:text-6xl text-navy tracking-tight">
                A standard you can verify.
              </h2>
            </div>
            <p className="text-muted-foreground xl:text-lg lg:max-w-sm xl:max-w-md">
              Every visit documented. Every checklist site-specific. Every result
              accountable.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5 xl:gap-8">
            {reasons.map((r) => (
              <div key={r.title} className={`bg-card rounded-2xl p-7 xl:p-10 shadow-card border border-border/60 hover-lift border-t-4 ${
                r.color === "mint" ? "border-t-mint" :
                r.color === "sky" ? "border-t-sky" :
                r.color === "deep" ? "border-t-navy-soft" : "border-t-ice"
              }`}>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-5 ${
                  r.color === "mint" ? "bg-mint/30 text-mint-ink" :
                  r.color === "sky" ? "bg-sky/30 text-navy-soft" :
                  r.color === "deep" ? "bg-secondary/80 text-navy-deep" : "bg-ice text-navy-soft"
                }`}>
                  <r.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 text-lg text-navy">{r.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl bg-secondary p-7 border border-sky/15">
              <div className="flex items-center gap-2 eyebrow text-navy font-bold">
                <Check className="w-4.5 h-4.5 text-navy-soft" /> Real-Time Reports (A Standard You Can Verify)
              </div>
              <ul className="mt-4 grid grid-cols-2 gap-y-2 text-sm text-foreground/85">
                {["Checklist-driven inspections", "Real-time service reports", "Issue tracking & follow-up", "Consistent quality accountability"].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-sky mt-2" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl bg-navy p-7 text-primary-foreground">
              <div className="flex items-center gap-2 eyebrow text-white font-bold">
                <Check className="w-4.5 h-4.5 text-mint" /> Our Service Promise
              </div>
              <ul className="mt-4 grid grid-cols-2 gap-y-2 text-sm text-white/90">
                {["Clear communication", "Consistent follow-through", "Site-specific checklists", "Practical pricing"].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-mint mt-2" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="gradient-navy-blue text-primary-foreground py-24 relative overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-1/2 stripe-accent-fade" aria-hidden />
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-10 items-end">
            <h2 className="font-display text-4xl md:text-5xl xl:text-6xl tracking-tight">
              Built for the properties you manage.
            </h2>
            <p className="text-primary-foreground/75 xl:text-lg lg:max-w-sm xl:max-w-md">
              Programs scaled to office towers, retail floors, strata corridors, and
              specialty environments.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 rounded-2xl overflow-hidden border border-primary-foreground/10">
            {sectors.map((s, i) => (
              <div
                key={s}
                className="group/sector p-8 xl:p-12 bg-primary-foreground/5 hover:bg-primary-foreground/10 transition border-primary-foreground/10"
                style={{ borderLeftWidth: i === 0 ? 0 : 1 }}
              >
                <div className="w-10 h-10 rounded-lg bg-primary-foreground/10 flex items-center justify-center mb-5 transition-transform group-hover/sector:scale-110">
                  <Sparkles className="w-4 h-4 text-sky group-hover/sector:text-mint transition-colors duration-300" />
                </div>
                <div className="font-display text-xl group-hover/sector:text-sky transition-colors duration-300">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-28 bg-cream">
        <div className="max-w-3xl xl:max-w-5xl mx-auto px-6 text-center">
          <Quote className="w-10 h-10 text-mint mx-auto" />
          <p className="mt-6 font-display text-2xl md:text-3xl xl:text-4xl text-navy leading-snug">
            "We needed a cleaning partner that was actually consistent, not just responsive after we complained. Aureline's digital check-ins and inspection reports mean we know our facilities are cleaned properly before the morning shift starts."
          </p>
          <div className="mt-8 text-sm xl:text-base font-semibold text-navy">
            Jatinder Cheema
            <span className="block text-xs text-muted-foreground font-normal mt-1">General Manager, Krown Produce</span>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-28">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 grid lg:grid-cols-2 gap-16 xl:gap-24">
          <div>
            <div className="eyebrow text-navy-soft">Contact Us</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl xl:text-6xl text-navy tracking-tight">
              Let's build a service plan for your space.
            </h2>
            <p className="mt-4 text-muted-foreground max-w-md xl:max-w-lg xl:text-lg">
              Tell us about your property — we'll put together a customized quote and
              walk you through next steps.
            </p>
            <div className="mt-10 space-y-4">
              {[
                { icon: Phone, t: "604-761-4449", href: "tel:6047614449" },
                { icon: Mail, t: "info@aurelinefs.com", href: "mailto:info@aurelinefs.com" },
                { icon: MapPin, t: "Unit 608 2818 Main Street, Vancouver BC V5T 0C1", href: undefined },
              ].map((c) => (
                <div key={c.t} className="flex items-center gap-3 text-sm">
                  <span className="w-9 h-9 rounded-full bg-sky/20 flex items-center justify-center">
                    <c.icon className="w-4 h-4 text-navy-soft" />
                  </span>
                  {c.href ? (
                    <a href={c.href} className="text-foreground hover:text-navy-soft transition font-medium">
                      {c.t}
                    </a>
                  ) : (
                    <span className="text-foreground">{c.t}</span>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-10 rounded-2xl bg-secondary p-5 flex items-center gap-4 max-w-sm">
              <span className="w-10 h-10 rounded-full bg-sky flex items-center justify-center">
                <Phone className="w-4 h-4 text-navy-deep" />
              </span>
              <div>
                <div className="text-sm text-navy-deep font-semibold">Get in touch</div>
                <div className="text-xs text-muted-foreground">For a customized quote.</div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-card rounded-2xl p-8 xl:p-12 shadow-card border border-border/60 space-y-5">
            {success && (
              <div className="bg-mint/20 border border-mint text-mint-ink rounded-lg p-4 text-sm animate-fade-in">
                Thank you! Your quote request has been sent successfully. We'll get back to you within one business day.
              </div>
            )}
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-4 text-sm animate-fade-in">
                {error}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <Field 
                label="Name" 
                required 
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              />
              <Field 
                label="Company / Property" 
                value={formData.company} 
                onChange={(e) => setFormData({ ...formData, company: e.target.value })} 
              />
              <Field 
                label="Email" 
                type="email" 
                required 
                value={formData.email} 
                onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
              />
              <Field 
                label="Phone" 
                type="tel" 
                value={formData.phone} 
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
              />
            </div>
            <div>
              <label className="eyebrow text-muted-foreground">Property Type</label>
              <select 
                value={formData.propertyType} 
                onChange={(e) => setFormData({ ...formData, propertyType: e.target.value })}
                className="mt-2 w-full h-11 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky/40 focus:border-sky/80 transition-all duration-200"
              >
                <option>Commercial office</option>
                <option>Retail</option>
                <option>Strata / Common areas</option>
                <option>Post-construction</option>
                <option>Specialty / Other</option>
              </select>
            </div>
            <div>
              <label className="eyebrow text-muted-foreground">Square Footage / Property Notes</label>
              <textarea 
                rows={4} 
                value={formData.notes} 
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="mt-2 w-full rounded-lg border border-input bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky/40 focus:border-sky/80 transition-all duration-200" 
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading} 
              className="w-full h-12 rounded-full bg-navy text-primary-foreground hover:bg-navy/90 flex items-center justify-center gap-2"
            >
              {loading ? "Sending..." : "Request a customized quote"} 
              {!loading && <ArrowRight />}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              We respond within one business day. No obligation.
            </p>
          </form>
        </div>
      </section>

    </Layout>
  );
}

function Field({ 
  label, 
  type = "text", 
  required = false, 
  value, 
  onChange 
}: { 
  label: string; 
  type?: string; 
  required?: boolean; 
  value: string; 
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; 
}) {
  return (
    <div>
      <label className="eyebrow text-muted-foreground">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="mt-2 w-full h-11 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky/40 focus:border-sky/80 transition-all duration-200"
      />
    </div>
  );
}
